
'use client';

import { useAuth } from '@/hooks/useAuth';
import { Loader2, PlusCircle, Newspaper, Edit, Trash2, Search, X, Users, PenLine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { createPeriodicoArticle, getAllPeriodicoArticles, updatePeriodicoArticle, deletePeriodicoArticle, getPeriodicoEditors, setPeriodicoEditors, searchUsersByNickname } from '@/lib/actions';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PeriodicoArticle } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { ArticleImageField } from '@/components/periodico/ArticleImageField';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Image from '@/components/ui/Image';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Card } from '@/components/ui/card';

function ArticleForm({ onFormSuccess, articleToEdit }: { onFormSuccess: () => void, articleToEdit?: PeriodicoArticle | null }) {
  const isEditing = !!articleToEdit;
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    
    let result;
    if (isEditing && articleToEdit) {
        result = await updatePeriodicoArticle(articleToEdit.id, formData);
    } else {
        result = await createPeriodicoArticle(formData);
    }
    
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Titular</Label>
          <Input id="title" name="title" defaultValue={articleToEdit?.title} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="subtitle">Subtítulo</Label>
          <Input id="subtitle" name="subtitle" defaultValue={articleToEdit?.subtitle} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         <div className="space-y-2">
            <Label htmlFor="authorName">Autor</Label>
            <Input id="authorName" name="authorName" defaultValue={articleToEdit?.authorName}/>
        </div>
        <div className="space-y-2">
            <Label htmlFor="category">Categoría</Label>
            <Input id="category" name="category" placeholder="Ej: Sucesos, Cultura..." defaultValue={articleToEdit?.category} />
        </div>
      </div>
      <ArticleImageField defaultValue={articleToEdit?.imageUrl} />
      <div className="space-y-2">
        <Label htmlFor="columns">Distribución del cuerpo</Label>
        <select
          id="columns"
          name="columns"
          defaultValue={String(articleToEdit?.columns || 1)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="1">1 columna (estándar)</option>
          <option value="2">2 columnas (estilo periódico)</option>
          <option value="3">3 columnas (compacto)</option>
        </select>
        <p className="text-xs text-muted-foreground">Cómo se reparte el texto del artículo al leerlo. En móvil siempre se muestra a 1 columna.</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Contenido del Artículo</Label>
        <RichTextarea id="content" name="content" rows={12} placeholder="Escribe aquí el cuerpo de la noticia. Deja una línea en blanco entre párrafos." defaultValue={articleToEdit?.content}/>
      </div>
       <DialogFooter>
            <DialogClose asChild>
                <Button variant="outline" type="button">Cancelar</Button>
            </DialogClose>
             <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Guardar Cambios' : 'Publicar Artículo'}
            </Button>
        </DialogFooter>
    </form>
  );
}

type FoundUser = { id: string; nickname: string; profileImageUrl: string | null };

function EditorsManager() {
  const { toast } = useToast();
  const [editors, setEditors] = useState<string[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getPeriodicoEditors().then(setEditors); }, []);

  useEffect(() => {
    const missing = editors.filter((u) => !names[u]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const fn = httpsCallable<{ uids: string[] }, { names: Record<string, string> }>(functions, 'getPublicUserNames');
        const res = await fn({ uids: missing });
        setNames((prev) => ({ ...prev, ...(res.data?.names || {}) }));
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editors.join(',')]);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      const { users } = await searchUsersByNickname(q);
      setResults(users || []);
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [term]);

  const persist = async (next: string[]) => {
    setSaving(true);
    const prev = editors;
    setEditors(next);
    const res = await setPeriodicoEditors(next);
    if (!res.success) { setEditors(prev); toast({ title: 'Error', description: res.message, variant: 'destructive' }); }
    setSaving(false);
  };

  const add = (u: FoundUser) => { if (!editors.includes(u.id)) { persist([...editors, u.id]); setNames((p) => ({ ...p, [u.id]: u.nickname })); } };
  const remove = (uid: string) => persist(editors.filter((e) => e !== uid));

  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-5 space-y-3">
      <div className="flex items-center gap-2">
        <PenLine className="h-5 w-5 text-blue-400" />
        <h3 className="font-bold">Redactores autorizados</h3>
        {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <span className="ml-auto text-xs text-muted-foreground">{editors.length}</span>
      </div>
      <p className="text-xs text-muted-foreground">Los usuarios añadidos verán la página <b>Redacción</b> en su menú y podrán redactar y editar artículos.</p>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar usuario para autorizar..." className="pl-9" />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {results.length > 0 && (
        <div className="space-y-1">
          {results.map((u) => {
            const already = editors.includes(u.id);
            return (
              <div key={u.id} className="flex items-center gap-2 rounded-xl border border-white/8 bg-muted/30 p-2">
                <Avatar className="h-8 w-8"><AvatarImage src={u.profileImageUrl || undefined} /><AvatarFallback>{u.nickname?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                <span className="flex-1 truncate text-sm">{u.nickname}</span>
                <Button size="sm" variant={already ? 'ghost' : 'secondary'} disabled={already} onClick={() => add(u)}>{already ? 'Ya está' : 'Añadir'}</Button>
              </div>
            );
          })}
        </div>
      )}

      {editors.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-2">Sin redactores autorizados.</p>
      ) : (
        <div className="space-y-1">
          {editors.map((uid) => (
            <div key={uid} className="flex items-center gap-2 rounded-xl border border-white/6 bg-muted/20 p-2">
              <Avatar className="h-8 w-8"><AvatarFallback>{(names[uid] || '?').charAt(0).toUpperCase()}</AvatarFallback></Avatar>
              <span className="flex-1 truncate text-sm">{names[uid] || uid}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => remove(uid)}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPeriodicoPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<PeriodicoArticle[]>([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [articleToEdit, setArticleToEdit] = useState<PeriodicoArticle | null>(null);
  const { toast } = useToast();

  const fetchArticles = async () => {
    setIsLoadingArticles(true);
    const { articles } = await getAllPeriodicoArticles();
    setArticles(articles);
    setIsLoadingArticles(false);
  };

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') {
      navigate('/jartinet');
    } else {
      fetchArticles();
    }
  }, [user, userData, loading, navigate]);

  const handleFormSuccess = () => {
    setIsDialogOpen(false);
    setArticleToEdit(null);
    fetchArticles();
  };

  const handleEditClick = (article: PeriodicoArticle) => {
    setArticleToEdit(article);
    setIsDialogOpen(true);
  };
  
  const handleNewClick = () => {
    setArticleToEdit(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (articleId: string) => {
    const result = await deletePeriodicoArticle(articleId);
    if (result.success) {
        toast({ title: 'Éxito', description: result.message });
        fetchArticles();
    } else {
        toast({ title: 'Error', description: result.error, variant: 'destructive' });
    }
  };

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="max-w-3xl">
          <h1 className="font-headline text-5xl font-bold text-primary">
            Gestionar Periódico
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Publica y gestiona los artículos del periódico "El Diario de Jartiland".
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if(!open) setArticleToEdit(null); setIsDialogOpen(open);}}>
          <DialogTrigger asChild>
            <Button size="lg" onClick={handleNewClick}>
              <PlusCircle className="mr-2 h-5 w-5" />
              Nuevo Artículo
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>{articleToEdit ? 'Editar Artículo' : 'Crear Nuevo Artículo'}</DialogTitle>
              <DialogDescription>
                Rellena todos los campos para publicar en el periódico.
              </DialogDescription>
            </DialogHeader>
            <ArticleForm onFormSuccess={handleFormSuccess} articleToEdit={articleToEdit} />
          </DialogContent>
        </Dialog>
      </div>

      <EditorsManager />

      <div className="space-y-4">
        <h2 className="text-2xl font-bold font-headline">Artículos Publicados</h2>
        {isLoadingArticles ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map(article => (
              <Card key={article.id} className="flex flex-col">
                 <div className="relative w-full aspect-video rounded-t-lg overflow-hidden">
                   <Image src={article.imageUrl} alt={article.title} className="object-cover w-full h-full" />
                 </div>
                 <div className="p-4 flex flex-col flex-grow">
                    <h3 className="font-bold text-lg leading-tight">{article.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">Por: {article.authorName}</p>
                    <p className="text-xs text-muted-foreground mt-auto pt-2">{format(new Date(article.createdAt), "dd/MM/yyyy", { locale: es })}</p>
                 </div>
                 <div className="p-2 border-t flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEditClick(article)}><Edit className="h-4 w-4" /></Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar este artículo?</AlertDialogTitle>
                            <AlertDialogDescription>Esta acción es permanente y no se puede deshacer.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(article.id)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                        </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                 </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 border-2 border-dashed rounded-lg">
            <Newspaper className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">No hay artículos</h3>
            <p className="mt-1 text-sm text-muted-foreground">Crea tu primer artículo para empezar.</p>
          </div>
        )}
      </div>
    </div>
  );
}
